import { TypeManagerDecorator } from "../../main/type.map.service";
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnInit,
    ViewChild,
    ViewEncapsulation,
    input,
} from "@angular/core";
import { jSith } from "../../util/jquery-replacement";
import { S25Util } from "../../util/s25-util";
import { AttachmentService } from "../../services/attachment.service";
import { InstanceCount } from "../../util/s25.instance.count";

export interface S25File {
    name?: string;
    size?: number;
    data?: string | ArrayBuffer;
    isChosen?: boolean;
    fileRef?: File;
    itemName?: string;
}

export interface FileList {
    readonly length: number;
    item(index: number): File | null;
    [index: number]: File;
}

@TypeManagerDecorator("s25-ng-file-upload")
@Component({
    selector: "s25-ng-file-upload",
    template: `
        <div>
            @if (isLoading) {
                <s25-ng-loading-inline-static></s25-ng-loading-inline-static>
            }

            @if (!isMobile) {
                <div
                    class="drop-zone"
                    (drop)="onDrop($event)"
                    (dragover)="onDragOver($event)"
                    (dragleave)="onDragLeave($event)"
                    [class.dragging]="isDragging"
                >
                    Drag and drop file here or click below to upload.
                </div>
            }

            <div class="upload-btn-wrapper">
                <input
                    #fileUpload
                    class="ngFileUpload"
                    type="file"
                    value="Choose a file"
                    [id]="fieldId()"
                    aria-label="Choose a file"
                    [attr.aria-invalid]="ariaInvalid() ? 'true' : null"
                    [attr.aria-describedby]="ariaDescribedby() || null"
                />
                <button class="file-btn" tabindex="-1" aria-hidden="true">Upload a file</button>
            </div>

            @if (hasShowFiles) {
                <div>
                    @for (file of files; track file) {
                        <div>
                            <label
                                ><input type="checkbox" class="input-checkbox" [(ngModel)]="file.isChosen" />{{
                                    file.name
                                }}</label
                            >
                        </div>
                    }
                </div>
            }
        </div>
    `,
    styles: [
        `
            .drop-zone {
                border: 2px dashed var(--color--border);
                padding: 15px;
                text-align: center;
                margin-bottom: 10px;
                transition:
                    background-color var(--transition-medium),
                    color var(--transition-medium),
                    border-color var(--transition-medium);
            }

            .drop-zone.dragging {
                background-color: var(--color--primary-flat-bg);
                border-color: var(--color--link-text);
                color: var(--color--link-text);
            }
        `,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class FileUploadComponent implements OnInit, AfterViewInit {
    private static uniqueId = InstanceCount.idGenerator({ prefix: "ngFileUpload" });

    @ViewChild("fileUpload") fileUpload: ElementRef<HTMLInputElement>;

    @Input() files?: S25File[]; //array caller can use to get access to uploaded files, { name: string, size: int (units: bytes), data: base64, isChosen: boolean }
    @Input() extRegex?: string = AttachmentService.FILE_EXTENSION_REGEX; //extension regex, if provided the file name must match this regex, eg: (\\.png|\\.jpg)$ would only allow png or jpg files
    @Input() maxSizeMb?: number = 25; //max file size allowed (by front-end) in MB
    @Input() multiple?: boolean = false;
    @Input() showFiles?: boolean = true;
    @Input() cleanFileName?: boolean = true;
    @Input() readAsArrayBuffer?: boolean = false;
    @Input() onAdd?: (file: S25File) => any; //called after file is added to array of files (file variable passed to function) -- use in templates as on-add="::self.FUNCTION(file)", eg use "file" parameter
    // TODO: consider creating something like s25Input.uniqueId({prefix: "ngFileUpload"})
    readonly fieldId = input(FileUploadComponent.uniqueId(), {
        transform: (value: string | null | undefined) => value || FileUploadComponent.uniqueId(),
    });
    readonly ariaInvalid = input<boolean>(false);
    readonly ariaDescribedby = input<string | undefined>(undefined);

    isLoading: boolean = false;
    hasShowFiles: boolean;
    init = false;

    isDragging = false;

    isMobile: boolean = S25Util.mobileCheck();

    constructor(
        private elementRef: ElementRef,
        private cd: ChangeDetectorRef,
    ) {}

    resetFileChooser = () => {
        if (this.fileUpload?.nativeElement) this.fileUpload.nativeElement.value = null;
        this.isLoading = false;
        this.cd.detectChanges();
    };

    reset = () => {
        this.resetFileChooser();
        Array.prototype.splice.apply(this.files, [0]);
        this.isLoading = false;
        this.cd.detectChanges();
    };

    ngAfterViewInit() {
        const fileElem = this.fileUpload.nativeElement;
        this.multiple && (fileElem.multiple = true);

        jSith.on(fileElem, "change.fileUpload", null, (changeEv: any) => {
            this.cleanFileName = S25Util.toBool(S25Util.coalesce(this.cleanFileName, true)); //somehow gets reset so we run the toBool/coalesce again...
            const files = changeEv?.target?.files;
            if (files && files.length) {
                this.processFileList(files);
            }
        });

        this.cd.detectChanges();
    }

    ngOnInit() {
        this.hasShowFiles = S25Util.toBool(S25Util.coalesce(this.showFiles, true)); //use new name bc showFiles could be truthy as a string of "false" but we want that converted to a bool before used
        this.cleanFileName = S25Util.toBool(S25Util.coalesce(this.cleanFileName, true)); //default to true; run toBool to convert truthy "false" string to actual false boolean
        this.files = this.files || ([] as S25File[]);
        this.multiple = S25Util.toBool(this.multiple);
        this.maxSizeMb = S25Util.parseInt(this.maxSizeMb) || undefined;
        this.init = true;
        this.cd.detectChanges();
    }

    onDragOver(event: DragEvent) {
        event.preventDefault();
        this.isDragging = true;
        this.cd.detectChanges();
    }

    onDragLeave(event: DragEvent) {
        event.preventDefault();
        this.isDragging = false;
        this.cd.detectChanges();
    }

    onDrop(event: DragEvent) {
        event.preventDefault();
        this.isDragging = false;
        const droppedFiles = event.dataTransfer?.files;
        if (droppedFiles && droppedFiles.length) {
            this.processFileList(droppedFiles);
        }
    }

    processFileList(fileList: S25File[] | FileList) {
        const MB = 1_000_000; // Use 1,000,000 works in Decimal  instead of 1024,

        //Check multiple file restriction first
        if (!this.multiple && fileList.length > 1) {
            this.resetFileChooser();
            alert("Please drag and drop one file only.");
            return;
        }

        jSith.forEach(fileList, (_, file) => {
            if (!file) return;

            // Extension validation
            if (this.extRegex && !file.name.toLowerCase().match(this.extRegex)) {
                this.showInvalidExtensionAlert();
                return;
            }

            //  File size validation
            if (this.maxSizeMb && file.size / MB > this.maxSizeMb) {
                this.showMaxSizeAlert();
                return;
            }

            this.readFile(file);
        });
    }

    /** Show alert when the extension does not match */
    showInvalidExtensionAlert(): void {
        const readableExtList = this.extRegex
            .toString()
            .replace(/[\\^$]/g, "") // remove regex control chars
            .split("|")
            .map((ext) => (ext.startsWith(".") ? ext : "." + ext))
            .join(", ");

        this.resetFileChooser();
        alert(`Invalid file extension. Must match: ${readableExtList}`);
    }

    /** Show alert when file exceeds max size */
    private showMaxSizeAlert(): void {
        this.resetFileChooser();
        alert(`File exceeds max size of ${this.maxSizeMb} MB`);
    }

    cleanName(name: string): string {
        let fileName = name;
        // Strip invalid characters
        fileName = fileName.replace(/[^0-9a-z_ \.]/gi, "");
        if (fileName.indexOf("\\") > -1) {
            fileName = fileName.split("\\").pop() ?? fileName;
        }
        return fileName;
    }

    /** Read the file and push to the files array */
    readFile(file: File): void {
        const cleanedName = this.cleanFileName ? this.cleanName(file.name) : file.name;

        // Create a safe file object with cleaned name
        const safeFile = new File([file], cleanedName, { type: file.type });

        const currFile: S25File = {
            name: cleanedName,
            size: S25Util.parseInt(safeFile.size),
            isChosen: true,
            fileRef: safeFile,
            data: undefined,
        };

        const reader = new FileReader();

        reader.onloadstart = () => {
            this.isLoading = true;
            this.cd.detectChanges();
        };

        reader.onload = () => {
            currFile.data = reader.result as string | ArrayBuffer;
            this.isLoading = false;
            if (!this.files) {
                this.files = [];
            }
            if (!this.multiple) {
                this.files.length = 0;
            }
            this.files.push(currFile);
            this.onAdd?.(currFile);
            this.cd.detectChanges();
        };

        reader.onerror = () => {
            this.isLoading = false;
            alert("The file could not be read. Code: " + (reader.error?.code ?? reader.error?.message));
            this.resetFileChooser();
        };

        if (this.readAsArrayBuffer) {
            reader.readAsArrayBuffer(safeFile);
        } else {
            reader.readAsDataURL(safeFile);
        }
    }
}
